import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

import { isPlatformIs24 } from '@/helpers/Platform/Platform';
import { BookQualifiedPropertyValuationResponse } from '@/models/mutations/bookQualifiedPropertyValuation';
import { PropertyDetails } from '@/models/queries/getPropertyDetails';
import { PropertyTypeNumber } from '@/models/valuationFunnel';
import { PropertyType, ValuationFlow } from '@/types';
import { HgSupportedLanguage } from '@smg-real-estate/domus';

import {
  Lead,
  Location,
  PostQualifiedValuationResult,
  PropertyValuationData,
  PropertyValuationState,
  RADIO_FIELDS,
} from '@/stores/types/propertyValuation';
import { fetchPropertyDetails, postQualifiedValuation, putLead } from './services';
import { FLOW_RULES } from './valuationFlowNavigator';

const createDefaultData = (): PropertyValuationData => ({
  propertyType: undefined,
  offerType: undefined,
  address: {
    lat: 0,
    lon: 0,
    countryCode: 'CH',
    postalCode: '',
    city: '',
    street: '',
    formattedAddress: '',
  },
  livingSpace: undefined,
  lotSize: undefined,
  rooms: 0,
  buildYear: undefined,
  isRenovated: undefined,
  renovationYear: undefined,
  isPropertyOwner: undefined,
  email: '',
  name: '',
  phoneNumber: '',
  whenToSell: undefined,
  culture: 'en-CH',
  leadId: undefined,
  estimationReason: '',
  utmSource: undefined,
  utmMedium: undefined,
  utmCampaign: undefined,
});

const createDefaultLead = (): Lead => ({
  homegate_lead_id: '',
  email: '',
  confirm_free_consultation: false,
});

export const getDefaultState = (): PropertyValuationState => ({
  step: 0,
  totalSteps: 0,
  previousStep: 0,
  loading: false,
  data: createDefaultData(),
  lead: createDefaultLead(),
  propertyDetailsData: [],
  selectedPropertyDetailIndex: 0,
  isValidLocation: undefined,
  locationAlternatives: [],
  isIframeActive: false,
});

// eslint-disable-next-line max-statements
export const usePropertyValuationStore = defineStore('propertyValuation', () => {
  const defaultState = getDefaultState();

  const step = ref<number>(defaultState.step);
  const totalSteps = ref<number>(defaultState.totalSteps);
  const previousStep = ref<number>(defaultState.previousStep);
  const loading = ref<boolean>(defaultState.loading);
  const data = ref<PropertyValuationData>(defaultState.data);
  const lead = ref<Lead>(defaultState.lead);
  const propertyDetailsData = ref<PropertyDetails[]>(defaultState.propertyDetailsData);
  const selectedPropertyDetailIndex = ref<number>(defaultState.selectedPropertyDetailIndex);
  const isValidLocation = ref<boolean | undefined>(defaultState.isValidLocation);
  const locationAlternatives = ref<Location[]>(defaultState.locationAlternatives);
  const isIframeActive = ref<boolean>(defaultState.isIframeActive);

  const isIv24FunnelActive = computed<boolean>(() => isPlatformIs24() && isIframeActive.value);

  const setFormLoading = (status: boolean): void => {
    loading.value = status;
  };

  const setTotalSteps = (value: number): void => {
    totalSteps.value = value;
  };

  const setFormStep = (value: number): void => {
    previousStep.value = step.value;
    step.value = value;
  };

  const setFormData = (payload: Partial<PropertyValuationData>): void => {
    data.value = { ...data.value, ...payload };
  };

  const setLead = (payload: Lead): void => {
    lead.value = payload;
  };

  const setPartialLocation = (payload: Partial<Location>): void => {
    data.value.address = { ...data.value.address, ...payload };
  };

  const setPropertyDetailsData = (value: PropertyDetails[]): void => {
    propertyDetailsData.value = value;
  };

  const setSelectedPropertyDetailIndex = (value: number): void => {
    selectedPropertyDetailIndex.value = value;
  };

  const setLocationValidity = (value = false): void => {
    isValidLocation.value = value;
  };

  const dropLocationValidity = (): void => {
    isValidLocation.value = undefined;
  };

  const setLocationAlternatives = (value: Location[]): void => {
    locationAlternatives.value = value;
  };

  const setIframeActive = (value: boolean): void => {
    isIframeActive.value = value;
  };

  const navigateBack = (currentStep: ValuationFlow): void => {
    const currentRule = FLOW_RULES[currentStep];
    const targetStep = currentRule.prev(data.value);

    if (targetStep === null) {
      return;
    }

    setFormStep(targetStep);

    const field = RADIO_FIELDS[targetStep as ValuationFlow];
    if (field) {
      setFormData({ [field]: undefined });
    }
  };

  const navigateNext = (currentStep: ValuationFlow): void => {
    const currentRule = FLOW_RULES[currentStep];
    const targetStep = currentRule.next(data.value);

    if (targetStep === null) {
      return;
    }

    if (currentRule.cleanupFields) {
      setFormData(currentRule.cleanupFields(data.value));
    }

    setFormStep(targetStep);
  };

  const setLocation = (location: Location): void => {
    setFormData({ address: location });
    setPropertyDetailsData([]);
    setSelectedPropertyDetailIndex(0);
  };

  const initData = (type: PropertyType): void => {
    setFormData({
      propertyType:
        type === PropertyType.Apartment ? PropertyTypeNumber.Apartment : PropertyTypeNumber.House,
    });
    setFormStep(1);
  };

  const resetForm = (): void => {
    setFormData(createDefaultData());
    setFormStep(0);
    setPropertyDetailsData([]);
    setSelectedPropertyDetailIndex(0);
  };

  const submitQualifiedValuation = async (): PostQualifiedValuationResult => {
    setFormLoading(true);

    let payload: PropertyValuationData & { source?: string } = data.value;
    if (isIframeActive.value) {
      payload = {
        ...payload,
        source: 'iv24_direct',
      };
    }

    const result = await postQualifiedValuation(payload);
    setFormLoading(false);

    return result;
  };

  const updateLead = async (payload: Lead): Promise<boolean> => {
    setFormLoading(true);
    const result = await putLead(payload);
    setFormLoading(false);

    return result;
  };

  const setLeadFromQualifiedValuation = (payload: BookQualifiedPropertyValuationResponse): void => {
    setLead({
      homegate_lead_id: payload.leadId,
      email: payload.email,
      confirm_free_consultation: false,
    });
  };

  const fetchPropertyDetailsData = async (locale: HgSupportedLanguage): Promise<void> => {
    const {
      address: { lat, lon, street, postalCode, city },
      propertyType,
      livingSpace: originalLivingSpace,
      buildYear: originalBuildYear,
      lotSize: originalLotSize,
      rooms: originalNumberOfRooms,
    } = data.value;
    const result = await fetchPropertyDetails({
      lat,
      lng: lon,
      locale,
      street,
      postalCode,
      city,
    });
    const sortedResult = [...result].sort((a, b) => {
      const floorA = a.floor ?? 0;
      const floorB = b.floor ?? 0;
      return floorA - floorB;
    });

    setPropertyDetailsData(sortedResult);
    setSelectedPropertyDetailIndex(0);

    if (result.length === 1) {
      const { buildYear, lotSize, numberOfRooms, livingSpace } = result[0];
      setFormData({
        livingSpace: livingSpace ?? originalLivingSpace,
        buildYear: buildYear ?? originalBuildYear,
        lotSize:
          propertyType === PropertyTypeNumber.Apartment ? undefined : (lotSize ?? originalLotSize),
        rooms: numberOfRooms ?? originalNumberOfRooms,
      });
    }
  };

  return {
    step,
    totalSteps,
    previousStep,
    loading,
    data,
    lead,
    propertyDetailsData,
    selectedPropertyDetailIndex,
    isValidLocation,
    locationAlternatives,
    isIframeActive,
    isIv24FunnelActive,
    setFormLoading,
    setTotalSteps,
    setFormStep,
    setFormData,
    setLead,
    setPartialLocation,
    setPropertyDetailsData,
    setSelectedPropertyDetailIndex,
    setLocationValidity,
    dropLocationValidity,
    setLocationAlternatives,
    setIframeActive,
    navigateBack,
    navigateNext,
    setLocation,
    initData,
    resetForm,
    submitQualifiedValuation,
    updateLead,
    setLeadFromQualifiedValuation,
    fetchPropertyDetailsData,
  };
});
